import { drawSignature, withRights } from "./pngMeta";
import { zoneLabel } from "./sections";
import { API, imageSrc } from "./api";

const NAME_KEY = "afae-share-name";
export const savedName = () => localStorage.getItem(NAME_KEY) || "";
export const saveName = (n) => localStorage.setItem(NAME_KEY, n);
export const cleanName = (n) => (n || "").replace(/[^\p{L}\p{N}\s'’.-]/gu, "").replace(/\s+/g, " ").trim().slice(0, 40);
export const shareUrl = (slug, name) => `${API}/share/${encodeURIComponent(slug)}${cleanName(name) ? `?from=${encodeURIComponent(cleanName(name))}` : ""}`;

export const loadImg = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

const tint = (hex, t) => {
  const n = parseInt((hex || "#BA68C8").slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * t));
  return `rgb(${c.join(",")})`;
};

export function drawLockup(ctx, W, H) {
  ctx.textAlign = "center";
  ctx.font = '500 17px "Cormorant Garamond"';
  ctx.letterSpacing = "8px";
  ctx.fillStyle = "#7E57C2";
  ctx.fillText("RAINBOW BY THE RAIN", W / 2 + 4, H - 158);
  ctx.letterSpacing = "0px";
  ctx.font = '400 56px "Parisienne"';
  ctx.fillStyle = "#4A2D55";
  ctx.fillText("Feeling Heart", W / 2, H - 98);
  ctx.font = '500 30px "Caveat"';
  ctx.fillStyle = "#8C6F66";
  ctx.fillText("an exhibition by Priyanka Jha", W / 2, H - 48);
}

export function fit(ctx, text, max) {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

export async function makeShareCard(painting, memory, name) {
  const t0 = performance.now();
  await Promise.all(
    ['300 84px "Cormorant Garamond"', 'italic 400 38px "Cormorant Garamond"', '500 46px "Caveat"', '600 24px "Plus Jakarta Sans"', '400 56px "Parisienne"', '500 20px "Cormorant Garamond"', '500 17px "Cormorant Garamond"'].map((f) => document.fonts.load(f)),
  ).catch(() => {});
  const img = await loadImg(imageSrc(painting.image_url));
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const pal = painting.palette?.length ? painting.palette : ["#BA68C8", "#4FC3F7", "#FFCA28"];
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, tint(pal[0], 0.78));
  g.addColorStop(0.55, tint(pal[1 % pal.length], 0.84));
  g.addColorStop(1, tint(pal[2 % pal.length], 0.76));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  const ar = img.naturalWidth / img.naturalHeight;
  const boxW = 860, boxH = 740, top = 96;
  let w = boxW, h = boxW / ar;
  if (h > boxH) {
    h = boxH;
    w = boxH * ar;
  }
  const x = (W - w) / 2, y = top + (boxH - h) / 2;
  ctx.save();
  ctx.shadowColor = "rgba(91,58,133,0.28)";
  ctx.shadowBlur = 50;
  ctx.shadowOffsetY = 24;
  ctx.fillStyle = "#fff";
  ctx.fillRect(x - 16, y - 16, w + 32, h + 32);
  ctx.restore();
  ctx.drawImage(img, x, y, w, h);
  drawSignature(ctx, x + w - w * 0.04, y + h - h * 0.035, Math.max(26, w * 0.055));

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  let ty = top + boxH + 100;
  const who = cleanName(name);
  ctx.font = '600 24px "Plus Jakarta Sans"';
  ctx.letterSpacing = "8px";
  ctx.fillStyle = "#7E57C2";
  ctx.fillText(painting.emotion.toUpperCase(), W / 2, ty);
  ctx.letterSpacing = "0px";
  if (zoneLabel(painting.section)) {
    ctx.font = 'italic 500 30px "Cormorant Garamond"';
    ctx.fillText(zoneLabel(painting.section), W / 2, ty - 42);
  }
  let size = 84;
  do {
    ctx.font = `300 ${size}px "Cormorant Garamond"`;
    size -= 4;
  } while (ctx.measureText(painting.title).width > 940 && size > 48);
  ctx.fillStyle = "#2A1B3D";
  ty += 92;
  ctx.fillText(fit(ctx, painting.title, 940), W / 2, ty);
  let line = null;
  if (memory) {
    ctx.font = '500 46px "Caveat"';
    ctx.fillStyle = "#4A3660";
    const q = ctx.measureText("“”").width;
    line = fit(ctx, memory.replace(/\s+/g, " ").trim(), 900 - q);
    ty += 74;
    ctx.fillText(`“${line}”`, W / 2, ty);
  }
  if (who) {
    ctx.font = '500 40px "Caveat"';
    ctx.fillStyle = "#7E57C2";
    ty += 62;
    ctx.fillText(fit(ctx, `from ${who}`, 900), W / 2, ty);
  }
  drawLockup(ctx, W, H);

  const blob = await withRights(await new Promise((r) => canvas.toBlob(r, "image/png")), `${painting.title} - Feeling Heart - Rainbow by the Rain`);
  const info = { width: W, height: H, ms: Math.round(performance.now() - t0), box: { w: Math.round(w), h: Math.round(h) }, ratio: w / h, imageRatio: ar, memoryLine: line, from: who || null, paintingBottom: Math.round(y + h + 16), textTop: top + boxH + 76 };
  window.__afaeShare = { last: info };
  return { blob, info };
}
